/**
 * Default slack (milliseconds) for async bots and indexing after the encounter
 * QuestionnaireResponse is written during labor encounter amendment.
 */
export const DEFAULT_ENCOUNTER_AMEND_BOT_LAG_MS = 180_000;

/** Toast title when a flowsheet edit is queued until Sign & Lock. */
export const LABOR_AMEND_QUEUED_TITLE = "Queued for amendment";

/** Toast body when a flowsheet edit is queued until Sign & Lock. */
export const LABOR_AMEND_QUEUED_MESSAGE =
  "This change applies when you click Sign & Lock. Cancel only discards queued edits that have not been saved yet.";

export const LABOR_AMEND_CANCEL_BLOCKED_TITLE = "Cannot discard saved flowsheet changes";
export const LABOR_AMEND_CANCEL_BLOCKED_MESSAGE =
  "Some flowsheet changes were already saved. Complete Sign & Lock to finish the encounter amendment, or leave this screen and resolve the encounter separately.";

export const LABOR_AMEND_PERSIST_LOCKED_TITLE = "Sign & Lock in progress";
export const LABOR_AMEND_PERSIST_LOCKED_MESSAGE =
  "Wait for Sign & Lock to finish before adding more flowsheet edits.";

export const LABOR_AMEND_POST_EFFECTS_FAILED_TITLE = "Amendment saved; side effects incomplete";
export const LABOR_AMEND_POST_EFFECTS_FAILED_MESSAGE =
  "The encounter amendment was saved, but follow-up updates (for example birth time display) could not be applied. Click Sign & Lock again to retry.";

export const LABOR_AMEND_POST_EFFECTS_LOST_TITLE = "Cannot complete amendment";
export const LABOR_AMEND_POST_EFFECTS_LOST_MESSAGE =
  "This page was refreshed after the encounter was saved but before follow-up updates finished. Return to the encounter and start a new amendment, or contact support if the flowsheet looks incomplete.";

export const LABOR_AMEND_PENDING_WORK_LOST_TITLE = "Unsaved flowsheet changes may be lost";
export const LABOR_AMEND_PENDING_WORK_LOST_MESSAGE =
  "This page was refreshed before Sign & Lock. Queued flowsheet edits that were not yet saved are no longer available. Review the flowsheet and re-enter any missing changes before signing.";

export const LABOR_AMEND_BEFOREUNLOAD_MESSAGE =
  "You have unsaved labor flowsheet changes. Leave anyway?";

/** Shared suffix for flowsheet persist failures during amend. */
export const LABOR_AMEND_NOT_FULLY_SAVED_SUFFIX = "Amendment was not fully saved.";

export const LABOR_AMEND_FLOWSHEET_SAVE_ERROR_QUICK_ADD_MESSAGE = `Failed to submit form. ${LABOR_AMEND_NOT_FULLY_SAVED_SUFFIX}`;

export const LABOR_AMEND_FLOWSHEET_SAVE_ERROR_EDIT_ROW_MESSAGE =
  "Failed to update form. Please try again.";

export const LABOR_AMEND_FLOWSHEET_SAVE_ERROR_STAGE_MOVE_MESSAGE = `Failed to move labor stage. ${LABOR_AMEND_NOT_FULLY_SAVED_SUFFIX}`;

export const LABOR_AMEND_FLOWSHEET_SAVE_ERROR_MILESTONE_MESSAGE = `Failed to update milestone time. ${LABOR_AMEND_NOT_FULLY_SAVED_SUFFIX}`;

export const LABOR_AMEND_FLOWSHEET_SAVE_ERROR_REMOVE_STAGE_MESSAGE = `Failed to cancel labor stage. ${LABOR_AMEND_NOT_FULLY_SAVED_SUFFIX}`;

export const LABOR_AMEND_FLOWSHEET_SAVE_ERROR_POSTPARTUM_MESSAGE = `Failed to update postpartum flowsheet. ${LABOR_AMEND_NOT_FULLY_SAVED_SUFFIX}`;

export const LABOR_AMEND_QUICK_ADD_SUCCESS_TITLE = "Success";
export const LABOR_AMEND_QUICK_ADD_SUCCESS_MESSAGE =
  "Form submitted successfully. The data will appear in the flowsheet shortly.";

export const LABOR_AMEND_ENCOUNTER_SAVE_FAILED_TITLE = "Failed to save encounter amendment";
export const LABOR_AMEND_ENCOUNTER_SAVE_FAILED_MESSAGE =
  "Flowsheet changes were saved. Click Sign & Lock again to finish the encounter amendment.";

export const LABOR_AMEND_LABOR_STAGES_LOAD_FAILED_TITLE = "Could not load labor stages";
export const LABOR_AMEND_LABOR_STAGES_LOAD_FAILED_MESSAGE =
  "Labor stage data failed to load. Refresh the page before editing the flowsheet so rows attach to the correct stage.";

export const LABOR_AMEND_LABOR_STAGES_REFRESH_FAILED_TITLE = "Flowsheet may be out of date";
export const LABOR_AMEND_LABOR_STAGES_REFRESH_FAILED_MESSAGE =
  "Your changes were saved, but labor stages could not be refreshed automatically. Refresh the page to see the latest data.";

export const LABOR_AMEND_BIRTH_LOAD_FAILED_TITLE = "Could not load birth time";
export const LABOR_AMEND_BIRTH_LOAD_FAILED_MESSAGE =
  "Birth time could not be loaded for this encounter. Flowsheet rows may be filtered incorrectly until you refresh the page.";

export const LABOR_AMEND_VALIDATION_ERROR_TITLE = "Validation Error";
export const LABOR_AMEND_VALIDATION_ERROR_MESSAGE = "Please select a reason for amendment.";

export const LABOR_AMEND_NOT_READY_TITLE = "Encounter data not ready";
export const LABOR_AMEND_NOT_READY_MESSAGE =
  "Encounter details are still loading. Try again in a moment.";

export const LABOR_AMEND_FLOWSHEET_FLUSH_FAILED_TITLE = "Failed to save flowsheet changes";

export const LABOR_AMEND_PROVENANCE_FAILED_TITLE = "Amendment saved with warnings";
export const LABOR_AMEND_PROVENANCE_FAILED_MESSAGE =
  "Your changes were saved, but we could not finish processing the amendment. Contact support if this continues.";

export const LABOR_AMEND_FLOWSHEET_SAVE_ERROR_TITLE = "Error";

export const LABOR_FLOWSHEET_VALIDATION_ERROR_MESSAGE =
  "Please review the highlighted fields and try again.";

export const LABOR_AMEND_PENDING_CHANGES_TITLE = "Pending flowsheet changes";
export const LABOR_AMEND_PENDING_CHANGES_MESSAGE =
  "Your edits won't be applied until you Sign & Lock encounter.";

export const LABOR_AMEND_FLOWSHEET_EDIT_MISSING_RESPONSE_TITLE = "Cannot update flowsheet row";
export const LABOR_AMEND_FLOWSHEET_EDIT_MISSING_RESPONSE_MESSAGE =
  "No existing questionnaire response was found for this row. Refresh the page and try again.";
